/**
 * The auth utility, made live (#576 L26).
 *
 * `app/src/pages/Auth.jsx` is presentational by contract: it owns its drafts, performs no fetch
 * and no navigation, and calls host callbacks. It serves four states — verify, forgot, reset and
 * setup. Three of them had no container, so `routeTable.js:62-63` routed `/auth` and `/auth/*`
 * into fixture mode and the screens could not do anything. `/verify-email` already has its own
 * container (`VerifyEmailRoute.jsx`) and is left exactly where it is.
 *
 * The ledger entry is L26, "Auth utility with warm invitation village vignette": "Keep
 * verify/forgot/reset/invite states distinct, expired/used/revoked handling and warm named
 * invitation scene. Preserve values on recoverable failure; no fake email or session success."
 *
 * **Every action returns a receipt the SERVER issued.** `Auth.jsx:468` runs each callback through
 * `persistWithReceipt`, which throws unless it gets `{receiptId, status}`, and
 * `QuestStudioRoute.jsx:192` sets the house rule that the value comes from the server rather than
 * from here. Two endpoints had nothing to give, so #576 added a per-request id to both — minted
 * before the account lookup branches, so a known and an unknown address answer identically.
 *
 * **A dead link is an ANSWER, not a fault.** An expired, used or unknown token comes back as
 * ordinary data and is rendered as the surface's calm error state. It is never reported as
 * "something went wrong", and it never names the household.
 */
import React, { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import LanternLoader from '../components/LanternLoader.jsx';
import { useActor } from '../state/ActorContext.jsx';
import {
  acceptInvitation,
  fetchInvitationPreview,
  requestPasswordReset,
  resetPassword,
  verifyResetToken,
} from '../api/session.js';

const Auth = lazy(() => import('../pages/Auth.jsx'));

/** Where a person who just arrived by invitation lands. */
export const LANDING_ROUTE = '/today';

/** The path suffix each state answers to, mirroring `redesign/mockups/auth.html`. */
export const STATE_BY_PATH = Object.freeze({
  'forgot-password': 'forgot',
  'reset-password': 'reset',
  'setup-account': 'setup',
});

/** Read the token out of the hash route's own query string. */
export function tokenFrom(search) {
  return new URLSearchParams(search || '').get('token') || '';
}

/** The state this path asks for, or `null` when the path is not one of ours. */
export function stateFrom(pathname) {
  const last = String(pathname || '').split('/').filter(Boolean).pop();
  return STATE_BY_PATH[last] || null;
}

/**
 * One sentence for a link that cannot be opened.
 *
 * The three reasons are kept apart deliberately — L26 asks for "expired/used/revoked handling" as
 * distinct states, and a person holding a genuinely expired link deserves to be told that rather
 * than "invalid". None of them names the family.
 */
export function deadLinkMessage(reason) {
  if (reason === 'expired') return 'This invitation has expired — ask whoever invited you for a fresh link.';
  if (reason === 'used') return 'This invitation was already used. If that was you, sign in instead.';
  return "This link can't be opened — it may have expired or already been used.";
}

const FAULT_MESSAGE = 'We could not check that link just now — please try it again in a moment.';

/**
 * Split a person's typed name without inventing anything.
 *
 * A single-word name stays a single word. `signup.service.js:922` writes `lastName || null`, and
 * #576 made `acceptInvitationSchema` agree, so a surname never has to be manufactured to satisfy
 * a check. Plenty of people have one name.
 */
export function splitName(name) {
  const trimmed = String(name || '').trim();
  const cut = trimmed.lastIndexOf(' ');
  if (cut <= 0) return { firstName: trimmed };
  return { firstName: trimmed.slice(0, cut), lastName: trimmed.slice(cut + 1) };
}

/**
 * The forgot-password action.
 *
 * Exported as a builder, the way `QuestStudioRoute.jsx:192` does, so the RECEIPT can be tested
 * without driving a form. `receiptId` is the id the SERVER minted. A container that returned a
 * value of its own would satisfy `persistWithReceipt` while attesting nothing.
 */
export function buildForgot(deps = {}) {
  const send = deps.requestPasswordReset || requestPasswordReset;
  return async (email) => {
    const answer = await send(email);
    const receiptId = String(answer?.requestId || '');
    if (!receiptId) throw new Error('the server did not confirm the reset request');
    return { receiptId, status: 'sent' };
  };
}

/** The reset action. Same receipt rule as `buildForgot`. */
export function buildReset(token, deps = {}) {
  const send = deps.resetPassword || resetPassword;
  return async ({ password }) => {
    const answer = await send(token, password);
    const receiptId = String(answer?.requestId || '');
    if (!receiptId) throw new Error('the server did not confirm the password change');
    return { receiptId, status: 'updated' };
  };
}

/** The invitation-accept action. The receipt is the real user id the server created. */
export function buildSetup({ token, invitation, setActor }, deps = {}) {
  const accept = deps.acceptInvitation || acceptInvitation;
  return async ({ name, password }) => {
    const { firstName, lastName } = splitName(name);
    const actor = await accept(token, {
      // The invitation names the address it was sent to, and this product's identifier IS the
      // email - `auth.service.js` branches on `identifier.includes('@')`. Taking it from the
      // invitation rather than from the form is what stops somebody claiming a different seat.
      username: invitation?.email || '',
      password,
      firstName,
      ...(lastName ? { lastName } : {}),
      tosAccepted: true,
    });
    const receiptId = String(actor?.userId ?? actor?.id ?? '');
    if (!receiptId) throw new Error('the server did not confirm the new account');
    if (typeof setActor === 'function') setActor(actor);
    return { receiptId, status: 'created' };
  };
}

export default function AuthRoute() {
  const location = useLocation();
  const { setActor } = useActor();

  const state = stateFrom(location.pathname);
  const token = tokenFrom(location.search);

  // `reset` and `setup` both arrive holding a token, and both must be CHECKED before the form is
  // shown. Collecting a password and only then refusing it is the experience this avoids.
  const needsToken = state === 'reset' || state === 'setup';

  const [checking, setChecking] = useState(needsToken);
  const [linkError, setLinkError] = useState(null);
  const [invitation, setInvitation] = useState(null);

  useEffect(() => {
    if (!needsToken) {
      setChecking(false);
      setLinkError(null);
      return undefined;
    }

    let alive = true;
    setChecking(true);
    setLinkError(null);

    (async () => {
      try {
        if (state === 'reset') {
          const verdict = await verifyResetToken(token);
          if (!alive) return;
          // The server answers 200 with its own verdict, so a refusal is data. `valid === false`
          // carries a message written for the person holding the link.
          if (verdict && verdict.valid === false) {
            setLinkError(verdict.message || deadLinkMessage('unknown'));
          }
        } else {
          const preview = await fetchInvitationPreview(token);
          if (!alive) return;
          if (!preview || preview.valid !== true) {
            setLinkError(deadLinkMessage(preview && preview.reason));
          } else {
            setInvitation(preview);
          }
        }
      } catch {
        // A transport fault is NOT a dead link, and saying so would send somebody to ask for a
        // replacement link that was never the problem.
        if (alive) setLinkError(FAULT_MESSAGE);
      } finally {
        if (alive) setChecking(false);
      }
    })();

    return () => { alive = false; };
  }, [needsToken, state, token]);

  // useMemo, not useCallback. The builders RETURN a function, and `useCallback(build(), [])` hands
  // the linter a function whose dependencies it cannot see — `react-hooks/exhaustive-deps` refuses
  // it by name. `useMemo` memoises the built function instead, which is what is actually wanted.
  const onForgot = useMemo(() => buildForgot(), []);
  const onReset = useMemo(() => buildReset(token), [token]);
  const onSetup = useMemo(() => buildSetup({ token, invitation, setActor }), [invitation, setActor, token]);

  const setupProps = useMemo(() => {
    if (state !== 'setup' || !invitation) return {};
    return {
      inviterName: invitation.inviterName,
      familyName: invitation.familyName,
      roleLabel: invitation.role === 'admin' ? 'Parent' : invitation.role === 'guest' ? 'Guest' : 'Family member',
      inviteEmail: invitation.email,
      inviteExpiryDays: null,
    };
  }, [invitation, state]);

  return (
    <Suspense
      fallback={
        <div style={{ padding: '2rem' }}>
          <LanternLoader label="Lighting the lanterns — loading the page." />
        </div>
      }
    >
      <Auth
        state={state || 'forgot'}
        loading={checking}
        error={linkError}
        onForgot={onForgot}
        onReset={onReset}
        onSetup={onSetup}
        {...setupProps}
      />
    </Suspense>
  );
}
